import React, { useState, useEffect, useMemo } from 'react';
import { ILocations } from 'utils/models/locations';
import ApiService from 'utils/services/ApiService';
import { CircularProgress, Button } from '@mui/material';
import Text from '../text';
import ExportResultsFilter from './ExportResultsFilter';
import PrimaryButton from '../button/PrimaryButton';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
	getTestResults,
	saveTestResults,
} from '../../utils/services/idbService';
import { convertDataLocation } from 'utils/functions/convertSIteDataintoLocations';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { Box, Typography } from '@mui/material';
import ExportFieldsChecklist, { ExportFieldOption } from '../../components/table-settings/ExportFieldsChecklist';
import { DataPoint } from '../../utils/models/data-point';

const GROUP_FIELD_ID = 'Group';

dayjs.extend(utc);

interface ExportFilters {
	locations: ILocations[];
	testKits: any[];
	fromDate: string;
	endDate: string;
	dates: string;
}

interface ExportProps {
	isOwner?: boolean;
	siteId: number;
	siteName: string;
	onClose: () => void;
	group: any;
	isOnline: boolean;
	tableColumnSettingsAllVOs?: TableColumnSettingsAllVO[];
	dataPoints?: DataPoint[];
	viewMode: string;
}

const GridIcon = ({ color }: { color: string }) => (
	<img src="/assets/icons/GridIcon.svg" alt="" width={24} height={24} style={{ filter: color === '#1976d2' ? 'invert(35%) sepia(94%) saturate(1200%) hue-rotate(185deg) brightness(88%) contrast(92%)' : 'none', opacity: color === '#1976d2' ? 1 : 0.45 }} />
);

const ListIcon = ({ color }: { color: string }) => (
	<img src="/assets/icons/ListIcon.svg" alt="" width={24} height={24} style={{ filter: color === '#1976d2' ? 'invert(35%) sepia(94%) saturate(1200%) hue-rotate(185deg) brightness(88%) contrast(92%)' : 'none', opacity: color === '#1976d2' ? 1 : 0.45 }} />
);

const TableIcon = ({ color }: { color: string }) => (
	<img src="/assets/icons/TableIcon.svg" alt="" width={24} height={24} style={{ filter: color === '#1976d2' ? 'invert(35%) sepia(94%) saturate(1200%) hue-rotate(185deg) brightness(88%) contrast(92%)' : 'none', opacity: color === '#1976d2' ? 1 : 0.45 }} />
);

const FORMAT_OPTIONS = [
	{
		id: 'matrix',
		icon: GridIcon,
		title: 'Matrix',
		description: 'One row per test, wide columns. Legacy format.',
	},
	{
		id: 'long',
		icon: ListIcon,
		title: 'Long',
		description: 'One row per result. Best for importing into another system.',
	},
	{
		id: 'table',
		icon: TableIcon,
		title: 'Table view',
		description: "Matches your site table's columns. Starts from what you're currently viewing — still editable.",
	},
];

const FORMAT_COLORS = {
	selectedBorder: '#1976d2',
	selectedBg: '#e8f4fd',
	selectedText: '#1976d2',
	defaultBorder: '#e0e0e0',
	defaultIcon: '#9e9e9e',
	defaultTitle: '#1a1a1a',
	bodyText: '#5f6368',
};

const SAMPLE_FIELDS: ExportFieldOption[] = [
	{ id: 'Operator name', colName: 'Operator name', colType: 'Sample', both: false},
	{ id: 'Operator email', colName: 'Operator email', colType: 'Sample', both: false},
	{ id: 'Notes', colName: 'Notes', colType: 'Sample', both: true },
	{ id: 'Test kit', colName: 'Test kit', colType: 'Sample', both: true },
	{ id: 'Location', colName: 'Location', colType: 'Sample', both: true },
	{ id: 'Group', colName: 'Group', colType: 'Sample', both: false},
	{ id: 'Device serial no.', colName: 'Device serial no.', colType: 'Sample', both: false},
	{ id: 'Device model', colName: 'Device model', colType: 'Sample', both: false},
];
interface FormatSelectorProps {
	value: string;
	onChange: (id: string) => void;
}
const FormatSelector: React.FC<FormatSelectorProps> = ({ value, onChange }) => {
	return (
		<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
			{FORMAT_OPTIONS.map(({ id, icon: Icon, title, description }) => {
				const isSelected = value === id;
				return (
					<Box
						key={id}
						role="button"
						tabIndex={0}
						onClick={() => onChange(id)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onChange(id);
							}
						}}
						sx={{
							flex: '1 1 220px',
							minWidth: 220,
							maxWidth: 320,
							p: 2.5,
							borderRadius: 2,
							border: '1.5px solid',
							borderColor: isSelected ? FORMAT_COLORS.selectedBorder : FORMAT_COLORS.defaultBorder,
							bgcolor: isSelected ? FORMAT_COLORS.selectedBg : '#fff',
							cursor: 'pointer',
							textAlign: 'left',
							transition: 'border-color 0.15s ease, background-color 0.15s ease',
							outline: 'none',
							'&:hover': {
								borderColor: isSelected ? FORMAT_COLORS.selectedBorder : '#bdbdbd',
							},
							'&:focus-visible': {
								boxShadow: `0 0 0 2px ${FORMAT_COLORS.selectedBorder}55`,
							},
						}}
					>
						<Box sx={{ mb: 1.5, lineHeight: 0 }}>
							<Icon color={isSelected ? FORMAT_COLORS.selectedText : FORMAT_COLORS.defaultIcon} />
						</Box>
						<Typography
							sx={{
								fontWeight: 600,
								fontSize: 15,
								color: isSelected ? FORMAT_COLORS.selectedText : FORMAT_COLORS.defaultTitle,
								mb: 0.5,
							}}
						>
							{title}
						</Typography>
						<Typography sx={{ fontSize: 13.5, lineHeight: 1.5, color: FORMAT_COLORS.bodyText }}>
							{description}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};

const Export: React.FC<ExportProps> = 
	({ siteName, 
		siteId, 
		onClose, 
		group,
		isOnline,
		tableColumnSettingsAllVOs, 
		dataPoints,
		viewMode
	}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [testKits, setTestKits] = useState<any[]>([]);
	const [locations, setLocations] = useState<ILocations[]>([]);
	const [loadingLocations, setLoadingLocations] = useState(false);
	const [exportFormat, setExportFormat] = useState('matrix');
	const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});
	const [savedLongExportSettings, setSavedLongExportSettings] = useState<any[] | null>(null);
	const [defaultKits, setDefaultKits] = useState<any[]>([]);
	const getDefaultFilters = (viewAll: boolean) => ({
		locations: [] as ILocations[],
		testKits: [] as any,
		fromDate: viewAll
			? dayjs().subtract(7300, 'day').startOf('day').format('YYYY-MM-DD')
			: dayjs().format('YYYY-MM-DD'),
		endDate: viewAll ? dayjs().endOf('day').format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
		dates: viewAll ? 'ALL TIME' : 'TODAY',
	});
	const isViewAll = useMemo(
		() => viewMode?.toLowerCase() === 'all' || viewMode?.toLowerCase() === 'view all',
		[viewMode]
	);
	const [filters, setFilters] = useState<ExportFilters>(() => getDefaultFilters(isViewAll));
	const [selectedPreset, setSelectedPreset] = useState(filters.dates);

	function parseScientificString(raw: string): number | null {
		const match = raw.trim().match(/^(-?\d+(\.\d+)?)\s*[x×]\s*10\^(-?\d+)$/i);
		if (!match) return null;
		const mantissa = parseFloat(match[1]);
		const exponent = parseInt(match[3], 10);
		return mantissa * Math.pow(10, exponent);
	}

	function trimTrailingZeros(numStr: string): string {
		if (!numStr.includes('.')) return numStr;
		return numStr.replace(/0+$/, '').replace(/\.$/, '');
	}

	function formatWithSignificance(value: number): string {
		const abs = Math.abs(value);

		if (abs >= 100) {
			return Math.round(value).toString();
		}

		if (abs === 0) {
			return '0';
		}

		const magnitude = Math.floor(Math.log10(abs));
		const decimals = Math.max(2, 1 - magnitude);
		return trimTrailingZeros(value.toFixed(decimals));
	}

	function formatScientific(value: number): string {
		const exponent = Math.floor(Math.log10(Math.abs(value)));
		const mantissa = value / Math.pow(10, exponent);
		const mantissaStr = trimTrailingZeros(mantissa.toFixed(2));
		return `${mantissaStr}E${exponent >= 0 ? '+' : ''}${exponent}`;
	}

	function formatExportValue(raw: any): string {
		if (raw === null || raw === undefined || raw === '') {
			return '';
		}

		let value: number;

		if (typeof raw === 'string') {
			const scientificValue = parseScientificString(raw);
			value = scientificValue !== null ? scientificValue : parseFloat(raw);
		} else {
			value = Number(raw);
		}

		if (!isFinite(value)) {
			return '0';
		}

		const abs = Math.abs(value);

		if (abs !== 0 && (abs >= 1e6 || abs < 1e-6)) {
			return formatScientific(value);
		}

		return formatWithSignificance(value);
	}

	function getSampleFieldDefaultVisibility(
		field: ExportFieldOption,
		tableColumnSettingsAllVOs: TableColumnSettingsAllVO[] | undefined
	): boolean {
		if (!tableColumnSettingsAllVOs) return field.both;

		const normalized = (value: string) => value?.trim().toLowerCase();
		const normalizedField = normalized(field.colName);

		if (normalizedField === 'group') {
			return false;
		}

		const findVisibleColumn = (columnName: string) =>
			tableColumnSettingsAllVOs.some(
				(col) => normalized(col.colName) === columnName && col.visible
			);

		if (normalizedField === 'operator name' || normalizedField === 'operator email') {
			return findVisibleColumn('operator');
		}

		if (normalizedField === 'test kit') {
			return findVisibleColumn('test kit');
		}

		if (normalizedField === 'location') {
			return findVisibleColumn('location');
		}

		if (normalizedField === 'notes') {
			return findVisibleColumn('notes');
		}

		if (normalizedField === 'device serial no.' || normalizedField === 'device serial no') {
			return false;
			//return findVisibleColumn('device');
		}

		if (normalizedField === 'device model') {
			return false;
			//return findVisibleColumn('device');
		}

		return field.both;
	}

	function buildExportFields(
		tableColumnSettingsAllVOs: TableColumnSettingsAllVO[] | undefined,
		dataPoints: DataPoint[] | undefined,
		selectedTestKitNames: Set<string>,
	): ExportFieldOption[] {
		const fields: ExportFieldOption[] = SAMPLE_FIELDS.map((field) => ({
			...field,
			both: getSampleFieldDefaultVisibility(field, tableColumnSettingsAllVOs),
		}));

		if (!tableColumnSettingsAllVOs) return fields;

		const outputColumnSettings = tableColumnSettingsAllVOs
			.filter((ele) => ele.colType === 'Input' || ele.colType === 'Output');

		outputColumnSettings.forEach((ele) => {
			const allMatchingDataPoints = (dataPoints ?? []).filter(
				(d) => d.displayName === ele.colName
			);

			if (allMatchingDataPoints.length === 0) return;

			const matchingSelectedKits = allMatchingDataPoints.filter((d) =>
				selectedTestKitNames.has(d.testkitName)
			);

			if (matchingSelectedKits.length === 0) return;

			let matchedUniqueKits: DataPoint[] = [];
			let matchedUniqueIDs: number[] = [];

			for (const match of matchingSelectedKits) {
				if (matchedUniqueIDs.includes(match.testkitId)) {
					continue;
				} else {
					matchedUniqueKits.push(match);
					matchedUniqueIDs.push(match.testkitId);
				}
			}

			for (const dp of matchedUniqueKits) {

				const label = dp.displayName || ele.colName;
				const unit = dp.outputUnit;

				if (fields.some((field) => field.id === `${label}_${unit ?? ''}`)) {
					continue;
				}
				
				fields.push({
					id: `${label}_${unit ?? ''}`,
					colName: unit ? `${label} (${unit})` : label,
					colType: ele.colType,
					both: ele.visible,
					outputId: (dp as any).outputId ?? null,
					testKit: dp.testkitId,
				});
			}
		});

		return fields;
	}
	useEffect(() => {
		const username = localStorage.getItem('currentUser') ?? 'unknown';
		getTestResults('defaultTestKits', username)
			.then((kits) => {
				setDefaultKits(Array.isArray(kits) ? kits : []);
			})
			.catch((error) => {
				console.error('Failed to load defaultTestKits for export payload matching', error);
			});
	}, []);

	const selectedTestKitNames = useMemo(
		() => new Set((filters.testKits as any[]).map((tk: any) => tk.testKitName)),
		[filters.testKits]
	);

	const exportFields: ExportFieldOption[] = useMemo(
		() => buildExportFields(tableColumnSettingsAllVOs, dataPoints, selectedTestKitNames),
		[tableColumnSettingsAllVOs, dataPoints, selectedTestKitNames, group]
	);

	const SAMPLE_EXPORT_FIELDS_MAP: Record<string, string> = {
		'operator name': 'Operator name',
		'operator email': 'Operator email',
		'notes': 'Notes',
		'test kit': 'Test kit',
		'location': 'Location',
		'group': 'Group',
		'device serial no.': 'Device serial no.',
		'device serial no': 'Device serial no.',
		'device model': 'Device model',
	};

	function normalizeValue(value?: string): string {
		return value?.trim().toLowerCase() ?? '';
	}

	function findLongExportFieldDescriptor(field: ExportFieldOption, allKits: any[]): any | null {
		if (!field || !Array.isArray(allKits) || allKits.length === 0) {
			return null;
		}

		const testKitId = field.testKit;
		const descriptorLookupId = field.colType === 'Input'
			? (field as any).inputId ?? (field as any).outputId ?? null
			: (field as any).outputId ?? null;

		if (testKitId == null || descriptorLookupId == null || descriptorLookupId === '') {
			return null;
		}

		const kit = allKits.find((candidateKit: any) => candidateKit.id === testKitId);
		if (!kit?.fieldDescriptorAllVOs) {
			return null;
		}

		const descriptor = kit.fieldDescriptorAllVOs.find((desc: any) => {
			const normalizedLookup = normalizeValue(String(descriptorLookupId));
			const normalizedDesc = normalizeValue(String(desc.outputId ?? desc.inputId ?? desc.id));
			return normalizedDesc === normalizedLookup;
		});

		return descriptor ?? null;
	}

	function buildLongExportFieldDescriptorId(field: ExportFieldOption, allKits: any[] = []): number | null {
		const descriptor = findLongExportFieldDescriptor(field, allKits);
		if (!descriptor || descriptor.id == null) {
			return null;
		}

		return Number(descriptor.id);
	}
 
	function mapSavedSettingToFieldId(savedField: any): string | null {
		if (savedField?.name) {
			return SAMPLE_EXPORT_FIELDS_MAP[normalizeValue(savedField.name)] ?? null;
		}

		
		if (savedField?.outputId != null && savedField?.testKitId != null) {
			const match = exportFields.find(
				(field) =>
					field.outputId != null &&
					field.testKit === Number(savedField.testKitId) &&
					normalizeValue(String(field.outputId)) === normalizeValue(String(savedField.outputId))
			);
			return match?.id ?? null;
		}

		if (savedField?.id != null) {
			const savedId = Number(savedField.id);
			const match = exportFields.find((field) => {
				const candidateId = buildLongExportFieldDescriptorId(field, defaultKits);
				return candidateId != null && Number(candidateId) === savedId;
			});
			return match?.id ?? null;
		}

		return null;
	}

	function applySavedLongExportSettings(savedFields: any[] | null | undefined, fields: ExportFieldOption[]): Record<string, boolean> {
		const next: Record<string, boolean> = {};

		if (!Array.isArray(savedFields) || savedFields.length === 0) {
			fields.forEach((field) => {
				next[field.id] = true;
			});
			return next;
		}

		fields.forEach((field) => {
			next[field.id] = field.colType === 'Sample' ? field.both : field.both;
		});

		savedFields.forEach((saved) => {
			const key = mapSavedSettingToFieldId(saved);
			if (!key) return;
			next[key] = !!saved.selected;
		});

		return next;
	}

	function buildLongExportSettingsPayload(
		selectedValues: Record<string, boolean>,
		fields: ExportFieldOption[],
		allKits: any[] = []
	) {
		return {
			exportType: 'Long',
			exportFields: fields.map((field) => {
				const selected = !!selectedValues[field.id];
				if (field.colType === 'Sample') {
					return {
						id: null,
						name: field.colName,
						type: field.colType,
						selected,
					};
				}

				const descriptorId = buildLongExportFieldDescriptorId(field, allKits);
				return {
					id: descriptorId,
					name: null,
					type: field.colType,
					selected,
				};
			}),
		};
	}

	async function loadSavedLongExportSettings() {
		try {
			const response = await ApiService.getExportSettings();
			const serverFields = response?.data?.exportFields ?? [];
			setSavedLongExportSettings(serverFields);
			setSelectedFields(applySavedLongExportSettings(serverFields, exportFields));
		} catch (error) {
			console.error('Failed to load long export settings', error);
			setSavedLongExportSettings([]);
			setSelectedFields(applySavedLongExportSettings([], exportFields));
		}
	}

	function buildDefaultSelectedFields(fields: ExportFieldOption[]): Record<string, boolean> {
		const next: Record<string, boolean> = {};
		fields.forEach((field) => {
			next[field.id] = field.both;
		});
		return next;
	}

	useEffect(() => {
		if (exportFormat === 'long') {
			loadSavedLongExportSettings();
		} else if (exportFormat === 'table') {
			setSelectedFields(buildDefaultSelectedFields(exportFields));
		}
	}, [exportFormat]);
	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			fromDate: isViewAll
				? dayjs().subtract(7300, 'day').startOf('day').format('YYYY-MM-DD')
				: dayjs().format('YYYY-MM-DD'),
			endDate: isViewAll
				? dayjs().endOf('day').format('YYYY-MM-DD')
				: dayjs().format('YYYY-MM-DD'),
			dates: isViewAll ? 'ALL TIME' : 'TODAY',
		}));
	}, [isViewAll]);
	const [filterErrors, setFilterErrors] = useState({
		locations: '',
		testKits: '',
		fromDate: '',
		endDate: '',
		dates: '',
	});

	const resetErrors = React.useCallback(() => {
		setFilterErrors({
			locations: '',
			testKits: '',
			fromDate: '',
			endDate: '',
			dates: '',
		});
	}, []);

	const handleFilterErrors = () => {
		if (filters.locations.length === 0) {
			setFilterErrors({
				...filterErrors,
				locations: 'Please select at least one location',
			});
			return false;
		}
		if (filters.testKits.length === 0) {
			setFilterErrors({
				...filterErrors,
				testKits: 'Please select at least one test kit',
			});
			return false;
		}
		return true;
	};

	const handleMatrixExport = async () => {
		const username = localStorage.getItem("currentUser") ?? "unknown";
		let dataPoints: any[] = [];
		const catchunits = await getTestResults("allDataPointsUnits", username);
		const units = { ...(catchunits[0] || {}) };

		if (dataPoints) {
			let cachedData = await getTestResults("allSites", username);
			const index = cachedData?.findIndex((ele: SiteData) => ele.id === siteId);
			if (index > -1) {
				cachedData[index]?.sampleLocationAllVOs?.forEach(
					(location: SampleLocationAllVO) => {
						if (filters.locations?.some((ele: any) => ele.id === location.id)) {
							location?.dataPointAllVOs?.forEach(
								(dp: DataPointAllVO, dpIndex: number) => {
									if (
										filters.testKits?.some(
											(ele: any) => ele.testKitName === dp.testKitName
										)
									) {
										dp.valueVOs.forEach((value) => {
											const timestamp = new Date(dp.timestamp);
											const fromTimestamp = new Date(filters.fromDate);
											const toTimestamp = new Date(filters.endDate);
											if (
												timestamp >= fromTimestamp &&
												timestamp <= toTimestamp
											) {
												dataPoints.push({
													samplePointId: location.id,
													testkitName: dp.testKitName,
													outputName: value.displayName,
													value: value.outputValue,
													notes: dp.note,
													uniqueKey: `testKitId-${dp.testKitId}-${dpIndex}`,
													outputId: value.outputId,
													timestamp: dp.timestamp,
													siteId: cachedData[index].id,
													siteName: cachedData[index].name,
													outputValue: value.outputValue,
													displayName: value.displayName,
													samplePointName: location.name,
													testKitId: dp.testKitId,
													unit: units[dp.testKitId]?.[value.outputId]?.unit
														? units[dp.testKitId]?.[value.outputId]?.unit
														: "-",
												});
											}else if(timestamp <= fromTimestamp &&
												timestamp <= toTimestamp){
												dataPoints.push({
													samplePointId: location.id,
													testkitName: dp.testKitName,
													outputName: value.displayName,
													value: value.outputValue,
													// notes: dp.note,
													uniqueKey: `testKitId-${dp.testKitId}-${dpIndex}`,
													outputId: value.outputId,
													// timestamp: dp.timestamp,
													siteId: cachedData[index].id,
													siteName: cachedData[index].name,
													// outputValue: value.outputValue,
													displayName: value.displayName,
													samplePointName: location.name,
													testKitId: dp.testKitId,
													unit: units[dp.testKitId]?.[value.outputId]?.unit
														? units[dp.testKitId]?.[value.outputId]?.unit
														: "-",
												});
											}
										});
									}
								}
							);
						}
					}
				);
			}
		}

		// Modified grouping logic to filter out empty rows
		const groupedByDate: { [key: string]: any[] } = {};
		dataPoints.forEach((dp) => {
			if (dp.outputValue) { // Only add data points that have values
				const dateKey = dp.timestamp ? dayjs(dp.timestamp).format("MM/DD/YYYY HH:mm:ss.SSS") : "";
				if (!groupedByDate[dateKey]) {
					groupedByDate[dateKey] = [];
				}
				groupedByDate[dateKey].push(dp);
			}
		});

		// Create headers for each location-testkit combination
		const locationTestKitMap = new Map<string, Set<string>>();
		const headersMap = new Map<string, string>();
		const subHeadersMap = new Map<string, string>();
		const unitsMap = new Map<string, string>();

		// Collect all unique headers regardless of data availability
		dataPoints.forEach((dp) => {
			const headerKey = `${dp.samplePointName}--${dp.testkitName}--${dp.outputId} (${dp.testKitId})`;
			headersMap.set(headerKey, dp.displayName);
			subHeadersMap.set(headerKey, `${dp.samplePointName}--${dp.testkitName}--${dp.outputId}`);
			unitsMap.set(headerKey, dp.unit || "-");
		});

		const includeGroup = !!selectedFields[GROUP_FIELD_ID];

		const headers = [
			"Site id#" + dataPoints[0]?.siteId,
			...(includeGroup ? ["Group"] : []),
			...Array.from(headersMap.values()),
			"Notes"
		];

		const subHeaderRow = [
			replaceQuotesCSVField("Site name#" + dataPoints[0]?.siteName),
			...(includeGroup ? [replaceQuotesCSVField(group?.name ?? '')] : []),
			...Array.from(subHeadersMap.values()).map(replaceQuotesCSVField),
			"Notes"
		];

		const unitsRow = [
			"MM/dd/yyyy HH:mm:ss.SSS",
			...(includeGroup ? ["-"] : []),
			...Array.from(unitsMap.values()).map(replaceQuotesCSVField),
			"-"
		];

		// Start the CSV content with headers
		let csvContent = "sep=,\n" + headers.join(",") + "\n" + subHeaderRow.join(",") + "\n" + unitsRow.join(",") + "\n";

		// Check if we have any data points within the date range
		const hasDataInRange = dataPoints.some(dp => {
			const timestamp = new Date(dp.timestamp);
			const fromTimestamp = new Date(filters.fromDate);
			const toTimestamp = new Date(filters.endDate);
			return timestamp >= fromTimestamp && timestamp <= toTimestamp;
		});

		// Only add data rows if we have data in range
		if (hasDataInRange) {
			Object.keys(groupedByDate)
				.sort((a, b) => a.localeCompare(b))
				.forEach((dateKey) => {
					const dataRow = groupedByDate[dateKey];
					const row = Array.from(headersMap.keys()).map((headerKey) => {
						const dp = dataRow.find(
							(dp) => `${dp.samplePointName}--${dp.testkitName}--${dp.outputId} (${dp.testKitId})` === headerKey
						);
						return replaceQuotesCSVField(formatExportValue(dp?.outputValue));
					});

					const uniqueNotes = new Set(dataRow.map((dp) => dp.notes).filter(Boolean));
					const notes = replaceQuotesCSVField(Array.from(uniqueNotes).join(" "));

					csvContent += [
						replaceQuotesCSVField(dateKey),
						...(includeGroup ? [replaceQuotesCSVField(group?.name ?? '')] : []),
						...row,
						notes
					].join(",") + "\n";
				});
		}

		handleDownloadCSV(csvContent);
	};
	async function handleTableFormatExport() {

		const username = localStorage.getItem("currentUser") ?? "unknown";
		let testEvents: any[] = [];

		let groups = await getTestResults("groupsInfo", username);
		let groupName = groups?.find((g: any) => g.sites?.some((s: any) => s.id === siteId)).name || "";

		let allKits = await getTestResults("defaultTestKits", username);

		let cachedData = await getTestResults("allSites", username);
		const index = cachedData?.findIndex((ele: SiteData) => ele.id === siteId);
		if (index > -1) {
			cachedData[index]?.sampleLocationAllVOs?.forEach(
				(location: SampleLocationAllVO) => {
					if (filters.locations?.some((ele: any) => ele.id === location.id)) {
						location?.dataPointAllVOs?.forEach(
							(dp: DataPointAllVO) => {
								if (
									filters.testKits?.some(
										(ele: any) => ele.testKitName === dp.testKitName
									)
								) {
									const timestamp = dp?.timestamp ? new Date(dp.timestamp) : null;
									const fromTimestamp = new Date(filters.fromDate);
									const toTimestamp = new Date(filters.endDate);
									if (
										timestamp &&
										timestamp >= fromTimestamp &&
										timestamp <= toTimestamp
									) {
										const values: Record<string, any> = {};
										dp.valueVOs.forEach((value) => {
											let unit = findUnitsForValue(dp.testKitId, value.outputId, allKits);
											let selectedFieldUnit = unit;
											if (unit === "RLU") {
												selectedFieldUnit = "";
											}
											let fieldLabel = value.displayName + "_" + selectedFieldUnit;
											values[fieldLabel] = value.outputValue;
										});
										testEvents.push({
											dataPointID: dp.id,
											siteId: cachedData[index].id,
											siteName: cachedData[index].name,
											timestamp: dp.timestamp,
											testkitName: dp.testKitName,
											samplePointName: location.name,
											operatorName: dp.operator,
											operatorEmail: dp.operatorEmail,
											deviceSerial: dp.deviceSerial ? formatSerialNumber(dp.deviceSerial.trim()) : '',
											deviceModel: getModelFromSerial(dp.deviceSerial),
											notes: dp.note,
											values,
										});
									}
								}
							}
						);
					}
				}
			);
		}

		testEvents.sort((a, b) => {
			const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
			const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
			return tA - tB;
		});

		const parameterFields = exportFields.filter(
			(field) => field.colType === 'Input' || field.colType === 'Output'
		);

		const selectedParameterFields = parameterFields.filter((field) => selectedFields[field.id]);

		let headerRecords: Record<string, boolean> = {
			"Test ID": true,
			"Site": true,
			"Timestamp": true,
			"Group": selectedFields["Group"],
			"Test kit": selectedFields["Test kit"],
			"Location": selectedFields["Location"],
			"Operator name": selectedFields["Operator name"],
			"Operator email": selectedFields["Operator email"],
			"Device serial no.": selectedFields["Device serial no."],
			"Device model": selectedFields["Device model"],
		};

		let selectedHeaders = Object.keys(headerRecords).filter(
			(key) => headerRecords[key] === true
		);

		const allHeaders = [
			...selectedHeaders,
			...selectedParameterFields.map((field) => field.colName),
			...(selectedFields["Notes"] ? ["Notes"] : []),
		];

		let csvContent = "sep=,\n" + allHeaders.map(replaceQuotesCSVField).join(",");

		testEvents.forEach((event) => {
			csvContent += "\n" +
				event.dataPointID + "," +
				replaceQuotesCSVField(event.siteName) + "," +
				dayjs(event.timestamp).format("MM/DD/YYYY HH:mm:ss.SSS") + "," +
				appendField(groupName, headerRecords["Group"]) +
				appendField(event.testkitName, headerRecords["Test kit"]) +
				appendField(event.samplePointName, headerRecords["Location"]) +
				appendField(event.operatorName, headerRecords["Operator name"]) +
				appendField(event.operatorEmail, headerRecords["Operator email"]) +
				appendField(event.deviceSerial, headerRecords["Device serial no."]) +
				appendField(event.deviceModel, headerRecords["Device model"]) +
				selectedParameterFields
					.map((field) => replaceQuotesCSVField(formatExportValue(event.values[field.id])))
					.join(",") +
				(selectedFields["Notes"] ? "," + replaceQuotesCSVField(event.notes) : "");
		});

		handleDownloadCSV(csvContent);

	}
	function formatSerialNumber(s: string): string {
		const matches = /^(PM\d+)-\d{2}(?:-(.*))?$/.exec(s);
		if (matches) {
			const [, PMSerialNumber, PBMID] = matches;
			return PBMID === undefined ? PMSerialNumber : `${PMSerialNumber}-${PBMID.startsWith("PBM1") ? "" : "PBM1"}${PBMID}`;
		}

		return s.replace(/^(PM\d+)-(?!PBM1)(.*)$/, "$1-PBM1$2");
	}

	async function handleLongFormatExport() {
		const username = localStorage.getItem("currentUser") ?? "unknown";
		let allKits = await getTestResults("defaultTestKits", username);
		await saveLongExportSettings(allKits);

		let dataPoints: any[] = [];
		
		let groups = await getTestResults("groupsInfo", username);
		let groupName = groups?.find((g: any) => g.sites?.some((s: any) => s.id === siteId)).name || "";

		if (dataPoints) {
			let cachedData = await getTestResults("allSites", username);
			const index = cachedData?.findIndex((ele: SiteData) => ele.id === siteId);
			if (index > -1) {
				cachedData[index]?.sampleLocationAllVOs?.forEach(
					(location: SampleLocationAllVO) => {
						if (filters.locations?.some((ele: any) => ele.id === location.id)) {
							location?.dataPointAllVOs?.forEach(
								(dp: DataPointAllVO, dpIndex: number) => {
									if (
										filters.testKits?.some(
											(ele: any) => ele.testKitName === dp.testKitName
										)
									) {
										dp.valueVOs.forEach((value) => {

											let unit = findUnitsForValue(dp.testKitId, value.outputId, allKits);
											let selectedFieldUnit = unit;
											if (unit === "RLU") {
												selectedFieldUnit = "";
											}
											let fieldLabel = value.displayName + "_" + selectedFieldUnit;

											if (selectedFields[fieldLabel] === undefined || selectedFields[fieldLabel] === false) {
												return;
											}

											const timestamp = dp?.timestamp ? new Date(dp.timestamp) : null;
											const fromTimestamp = new Date(filters.fromDate);
											const toTimestamp = new Date(filters.endDate);
											if (
												timestamp &&
												timestamp >= fromTimestamp &&
												timestamp <= toTimestamp
											) {
												dataPoints.push({
													samplePointId: location.id,
													testkitName: dp.testKitName,
													outputName: value.displayName,
													value: value.outputValue,
													notes: dp.note,
													uniqueKey: `testKitId-${dp.testKitId}-${dpIndex}`,
													outputId: value.outputId,
													timestamp: dp.timestamp,
													siteId: cachedData[index].id,
													siteName: cachedData[index].name,
													outputValue: value.outputValue,
													displayName: value.displayName,
													samplePointName: location.name,
													testKitId: dp.testKitId,
													dataPointID: dp.id,
													operatorName: dp.operator,
													operatorEmail: dp.operatorEmail,
													deviceSerial: dp.deviceSerial? formatSerialNumber(dp.deviceSerial.trim()) : '',
													deviceModel: getModelFromSerial(dp.deviceSerial),
													unit: unit,
												});
											}
										});
									}
								}
							);
						}
					}
				);
			}
		}
		
		const groupedByDate: { [key: string]: any[] } = {};
		dataPoints.forEach((dp) => {
			if (dp.outputValue) { // Only add data points that have values
				const dateKey = dp.timestamp ? dayjs(dp.timestamp).format("MM/DD/YYYY HH:mm:ss.SSS") : "";
				if (!groupedByDate[dateKey]) {
					groupedByDate[dateKey] = [];
				}
				groupedByDate[dateKey].push(dp);
			}
		});

		// Sort dataPoints by timestamp before grouping
		dataPoints.sort((a, b) => {
			const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
			const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
			return tA - tB;
		});

		let headerRecords: Record<string, boolean> = {
			"Test ID": true,
			"Site": true,
			"Timestamp (GMT)": true,
			"Group": selectedFields["Group"],
			"Test kit": selectedFields["Test kit"],
			"Location": selectedFields["Location"],
			"Operator name": selectedFields["Operator name"],
			"Operator email": selectedFields["Operator email"],
			"Device serial no.": selectedFields["Device serial no."],
			"Device model": selectedFields["Device model"],
			"Parameter": true,
			"Value": true,
			"Unit": true,
			"Notes": selectedFields["Notes"],
		};

		let selectedHeaders = Object.keys(headerRecords).filter(
			(key) => headerRecords[key] === true
		);

		let csvContent = "sep=,\n" + selectedHeaders.join(",");

		dataPoints.forEach((dp) => {
			csvContent += "\n" + 
				dp.dataPointID + "," + 
				dp.siteName + "," + 
				formatDateUTC(dp.timestamp) + "," + 
				appendField(groupName, headerRecords["Group"]) +
				appendField(dp.testkitName, headerRecords["Test kit"]) + 
				appendField(dp.samplePointName, headerRecords["Location"]) +
				appendField(dp.operatorName, headerRecords["Operator name"]) + 
				appendField(dp.operatorEmail, headerRecords["Operator email"]) +
				appendField(dp.deviceSerial, headerRecords["Device serial no."]) +
				appendField(dp.deviceModel, headerRecords["Device model"]) +
				dp.displayName + "," + 
				formatExportValue(dp.value) + "," +
				dp.unit + "," + 
				appendLastField(dp.notes, headerRecords["Notes"]);
		})

		handleDownloadCSV(csvContent);
		
	}

	async function saveLongExportSettings(allKits: any[] = []) {
		if (!isOnline) return;
		try {
			const payload = buildLongExportSettingsPayload(selectedFields, exportFields, allKits);
			await ApiService.saveExportSettings(payload);
		} catch (error) {
			console.error('Failed to save long export settings', error);
		}
	}

	function formatDateUTC(date: string | number | Date): string {
		let formatted = date ? dayjs(date).format("MM/DD/YYYY HH:mm:ss.SSS") : "";

		const utcDate = dayjs.utc(formatted);
		formatted = utcDate.format("MM/DD/YYYY HH:mm:ss.SSS");

		return formatted;
	}

	function findUnitsForValue(testKitId: number, outputId: string, allKits: any[]): string {

		let fieldDescriptor = allKits?.find((kit: any) => kit.id === testKitId)?.fieldDescriptorAllVOs?.find((desc: any) => desc.outputId === outputId) ?? null;
		if (fieldDescriptor?.unit) {
			return fieldDescriptor.unit;
		}

		if (fieldDescriptor?.type !== "Output") {
			return "RLU";
		}

		return "";
													
	}

	function getModelFromSerial(serialValue: string | undefined): string {

		if (!serialValue) return '';

		if (serialValue.trim().startsWith("BC")) {
			return "Bugcount One";
		} else {
			return "PhotonMaster & PBM"
		}

	}

	function appendField(entry: string, shouldAppend: boolean) {
		return shouldAppend ? `${entry},` : '';
	}

	function appendLastField(entry: string, shouldAppend: boolean) {
		return shouldAppend ? entry : '';
	}

	const handleDownloadCSV = (csvContent: string) => {
		const encoder = new TextEncoder();
		const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
		const body = encoder.encode(csvContent);
		const blob = new Blob([bom, body], {
			type: 'text/csv;charset=utf-8;',
		});
		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.setAttribute(
			'download',
			`LuminUltra Data Export - ${siteName} - ${dayjs().format('YYYYMMDD')}.csv`
		);
		document.body.appendChild(link);
		link.click();
		link.remove();

		// Release the URL
		URL.revokeObjectURL(url);
	};

	const handleExport = async () => {
		if (!handleFilterErrors()) {
			return;
		}

		setIsLoading(true);
		
		try {
			if (exportFormat === 'long') {
				await handleLongFormatExport();
			} else if (exportFormat === 'table') {
				await handleTableFormatExport();
			} else {
				await handleMatrixExport();
			}
			addEventLog("Report download Success", "Site Management", siteId);
		} catch (error) {
			console.error('Error exporting data:', error);
			addEventLog("Report download Failed", "Site Management", siteId);
		}

		setIsLoading(false);
	};
	const setTestKitsBasedOnLocations = (location: ILocations[]) => {
		const uniqueData: any[] = [...(location || [])]
			?.flatMap(({ testKitDescriptors }: any) => testKitDescriptors || [])
			?.filter(
			(v: any, i: number, a: any) =>
				a?.findIndex((t: any) => t.id === v.id) === i
			);

		setTestKits([...uniqueData] as any);

		setFilters((prevFilters: ExportFilters) => ({
			...prevFilters,
			testKits: uniqueData,
		}));
  	};
	const getLocations = async () => {
		setLoadingLocations(true);
		const username = localStorage.getItem('currentUser') ?? 'unknown';
		const storedLocations = await getTestResults(
			`locations_${siteId}`,
			username
		);

		// if (storedLocations && storedLocations.length > 0) {
			// setLocations(storedLocations);
			// setTestKitsBasedOnLocations(storedLocations)
			// setLoadingLocations(false);
		// } else {
			try {
				if (isOnline) {
					const result = await ApiService.getLocationBySiteID(siteId);
					if (result.status !== 200)
						throw Error('Error to connect with the server.');
					setLocations(result.data);
					setTestKitsBasedOnLocations(result.data)
					await saveTestResults(
						`locations_${siteId}`,
						result.data,
						username
					);
					setLoadingLocations(false);
				} else {
					const cachedData = await getTestResults(
						`allSites`,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					const defaultTestKits = await getTestResults(`defaultTestKits`, localStorage.getItem('currentUser') ?? 'unknown');
					const SiteIndex = cachedData.findIndex((site: any)=> site.id === siteId)
					const offlineData = convertDataLocation(cachedData[SiteIndex],defaultTestKits)
					if (Array.isArray(offlineData)) {
						const extractedLocations = offlineData
							.filter(
								(location) =>
									location.testKitDescriptors?.some(
										(tk: { testKitName: any; id: any }) =>
											tk.testKitName && tk.id
									)
							)
							.map((location) => ({
								id: location.id,
								name: location.name,
								userId: 0,
								testKitDescriptions: [],
								testKitDescriptors: location.testKitDescriptors
									.filter(
										(tk: { testKitName: any; id: any }) =>
											tk?.testKitName && tk?.id
									)
									.map(
										(tk: {
											riskScoreVO: any;
											testKitName: any;
											id: any;
											testKitInputDescriptorVOs: any;
											testKitOutputDescriptorVOs: any;
											outPutAlarmsVos: any;
										}) => {
											return {
												testKitName: tk?.testKitName,
												id: tk?.id,
												outPutAlarmsVos:
													location?.testKitDescriptors[0]
														?.outPutAlarmsVos[0],
												testKitInputDescriptorVOs:
													tk?.testKitInputDescriptorVOs,
												testKitOutputDescriptorVOs:
													tk?.testKitOutputDescriptorVOs,
												riskScoreVO: tk?.riskScoreVO,
											};
										}
									),
							}));
						setLocations(extractedLocations);
						setTestKitsBasedOnLocations(extractedLocations);
						setLoadingLocations(false);
					}

				}
			} catch (error) {
				console.log(error);
				setLoadingLocations(false);
			}
		// }
	};
	function replaceQuotesCSVField(value: string | number | null | undefined): string {
		const str = String(value ?? '');
		if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r'))
			return '"' + str.replace(/"/g, '""') + '"';
		return str;
	}
	useEffect(() => {
		getLocations();
	}, []);
return (
	<>
		<div id="export">
			
			<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
				Export Results to CSV
			</Text>
			<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[24px]">
				Choose locations and test kits
			</Text>
			<div className="pt-[16px]">
				<FormatSelector value={exportFormat} onChange={setExportFormat} />
			</div>
			<div className="pt-[16px]">
				{loadingLocations ? (
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
						<CircularProgress />
					</div>
				) : (
					<div>
						<ExportResultsFilter
							locations={locations}
							testKits={testKits}
							setTestKits={setTestKits}
							setFilters={setFilters}
							filters={filters}
							filterErrors={filterErrors}
							resetErrors={resetErrors}
							isViewAll={isViewAll}
						/>
					</div>
				)}
				{/* Action Buttons */}
				{(exportFormat === 'long' || exportFormat === 'table') && (
					<div className="pt-[16px]">
						<ExportFieldsChecklist
							format={exportFormat}
							fields={exportFields}
							selectedValues={selectedFields}
							onChange={setSelectedFields}
						/>
					</div>
				)}

				<div className="mt-6 pt-4 flex justify-end space-x-1 items-center">
					<Button onClick={onClose} color="primary" className="!text-[#9EACB4] cancel-export">
						CANCEL
					</Button>
					{isLoading ? (
						<CircularProgress sx={{ color: '#0092C8' }} size={22} className="!mr-5" />
					) : (
						<PrimaryButton addStyle="!h-[36px] !w-[68px] export" onClick={handleExport}>
							EXPORT
						</PrimaryButton>
					)}
				</div>
			</div>
		</div>
	</>
);
}
export default Export;